import { Outlet, createRootRoute, useLocation, Link } from '@tanstack/react-router'
import { QueryProvider } from '@/providers/query-provider'
import { ThemeProvider } from '@/providers/theme-provider'
import { LenisProvider, useLenis } from '@/providers/lenis-provider'
import { ConsentProvider } from '@/providers/consent-provider'
import { CookieConsent } from '@/components/CookieConsent'
import { HubSpotTracking } from '@/components/HubSpotTracking'
import { FontLoader } from '@/lib/shared'
import { motion, AnimatePresence } from 'motion/react'
import { useState, useEffect, useRef } from 'react'
import { EASE_OUT, BODY_FONT } from '@/lib/constants'
import { useIsMobile, canHover } from '@/lib/hooks'
import { ProgressiveBlurTop } from '@/components/ProgressiveBlurTop'
import { SiteFooter } from '@/components/SiteFooter'
import { ErrorPage } from '@/components/ErrorPage'
import { useErrorPage } from '@/lib/errorPageState'
import { CtaButton } from '@/components/home/CtaButton'
import { COLORS, NEUTRAL8_RGB } from '@/components/home/tokens'
import { NavDropdown } from '@/components/NavDropdown'
import { ROUTE_META, SITE, canonicalUrl, isUnlistedRoute, pressIndexCanonicalUrl } from '@/lib/seo'
import 'lenis/dist/lenis.css'
import '@/styles/tailwind.css'
import '@/styles/app.css'
import '@/styles/globals.css'
import '@/styles/typography.css'

function NotFoundPage() {
  return (
    <ErrorPage
      code="404"
      title="This page doesn't exist."
      message="The page you're looking for may have moved or never existed."
      documentTitle="Page Not Found — Ent"
    />
  )
}

function RouteErrorPage() {
  return (
    <ErrorPage
      code="500"
      title="Something went wrong."
      message="An unexpected error occurred. Please try again, or head back home."
      documentTitle="Error — Ent"
    />
  )
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorPage,
})

// Per-route SEO metadata — shared with the build-time prerender script.
const routeMeta = ROUTE_META

// Animatable anchor — a crawlable <Link> that supports motion props.
const MotionLink = motion.create(Link)

const resetButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
}

function NavLink({
  label,
  accentColor,
  to,
  href,
  onClick,
  caret,
  restColor,
}: {
  label: string
  accentColor: string
  /** Internal route — renders a crawlable <a> via <Link>. */
  to?: string
  /** External URL — renders a target=_blank anchor. */
  href?: string
  onClick?: () => void
  caret?: boolean
  /** Text color in the rest state (defaults to accentColor). */
  restColor?: string
}) {
  const [hovered, setHovered] = useState(false)
  const textColor = restColor ?? accentColor

  const hoverHandlers = {
    onMouseEnter: canHover ? () => setHovered(true) : undefined,
    onMouseLeave: canHover ? () => setHovered(false) : undefined,
  }
  const sharedStyle: React.CSSProperties = {
    ...resetButton,
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    textDecoration: 'none',
  }

  const inner = (
    <>
      <motion.span
        animate={{ color: textColor }}
        transition={{ duration: 1, ease: EASE_OUT }}
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 500,
          fontFamily: BODY_FONT,
        }}
      >
        {label}
      </motion.span>
      {caret && (
        <motion.span
          animate={{ color: textColor }}
          transition={{ duration: 1, ease: EASE_OUT }}
          style={{ fontSize: '9px', lineHeight: 1, transform: 'translateY(1px)' }}
        >
          ▼
        </motion.span>
      )}
      <motion.span
        animate={{
          backgroundColor: accentColor,
          scaleX: hovered ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: EASE_OUT }}
        style={{
          position: 'absolute',
          bottom: '-4px',
          left: 0,
          right: 0,
          height: '1px',
          borderRadius: '9999px',
          transformOrigin: 'left',
        }}
      />
    </>
  )

  // Internal route → crawlable anchor with client-side nav.
  if (to) {
    return (
      <Link to={to} {...hoverHandlers} style={sharedStyle}>
        {inner}
      </Link>
    )
  }
  // External link → real anchor.
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...hoverHandlers} style={sharedStyle}>
        {inner}
      </a>
    )
  }
  return (
    <button onClick={onClick} {...hoverHandlers} style={sharedStyle}>
      {inner}
    </button>
  )
}

// Color schemes for each page
const pageColors = {
  '/': {
    background: COLORS.neutral8,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/blog': {
    background: COLORS.neutral9,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/news': {
    background: COLORS.neutral9,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/platform': {
    background: COLORS.neutral8,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/contact': {
    background: COLORS.neutral8,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/company': {
    background: COLORS.neutral8,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/privacy': {
    background: COLORS.neutral2,
    accent: COLORS.neutral8,
    logoColor: COLORS.neutral8,
  },
  '/terms': {
    background: COLORS.neutral4,
    accent: COLORS.neutral8,
    logoColor: COLORS.neutral8,
  },
  '/subscription': {
    background: COLORS.neutral6,
    accent: COLORS.neutral2,
    logoColor: COLORS.neutral2,
  },
  '/sub-processors': {
    background: COLORS.neutral8,
    accent: COLORS.neutral2,
    logoColor: COLORS.neutral2,
  },
  '/dpa': {
    background: COLORS.neutral9,
    accent: COLORS.neutral2,
    logoColor: COLORS.neutral2,
  },
  '/joinus': {
    background: COLORS.neutral8,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
  '/partners': {
    background: COLORS.neutral8,
    accent: COLORS.green1,
    logoColor: COLORS.green1,
  },
}

function RootComponent() {
  return (
    <LenisProvider>
      <RootContent />
    </LenisProvider>
  )
}

function RootContent() {
  const location = useLocation()
  // Normalize away trailing slashes so route matching (the flags below, page
  // colors, etc.) works regardless of how the host serves the URL — directory
  // routes are served with a trailing slash in production (/platform → /platform/),
  // which would otherwise miss every exact-match check.
  const pathname = location.pathname.replace(/\/+$/, '') || '/'

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const isMobile = useIsMobile()

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
    setResourcesOpen(false)
  }

  // Whether the user has scrolled past the hero (drives the nav rune reveal).
  const [pastHero, setPastHero] = useState(false)
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.6)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Disable browser scroll restoration so we always start at top
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
  }, [])

  const lenis = useLenis()

  // Animation values
  const isLanding = pathname === '/'

  // Check if contact form was submitted (check the search object from TanStack Router)
  const searchParams = location.search as { submitted?: boolean }
  const isContactSubmitted = pathname === '/contact' && searchParams.submitted === true

  // Logo lockup: full rune + wordmark on every page; on the homepage the rune
  // only appears once scrolled past the hero.
  const showRune = !isLanding || pastHero

  // Listen for investor overlay to change nav/footer colors
  const [investorOverlay, setInvestorOverlay] = useState(false)
  useEffect(() => {
    const handler = (e: Event) => {
      setInvestorOverlay((e as CustomEvent).detail.active)
    }
    window.addEventListener('investor-overlay', handler)
    return () => window.removeEventListener('investor-overlay', handler)
  }, [])
  // Listen for landing page continuous background color
  const [landingBg, setLandingBg] = useState<string | null>(null)
  useEffect(() => {
    const handler = (e: Event) => {
      setLandingBg((e as CustomEvent).detail.color)
    }
    window.addEventListener('landing-bg', handler)
    return () => window.removeEventListener('landing-bg', handler)
  }, [])

  // Hide the global footer (and apply the error title) while an error page is shown
  const errorPage = useErrorPage()
  const isErrorPage = errorPage.active

  // Reset overlays on route change
  // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing transient overlay state in response to navigation. `pathname` comes from the router (an external system); this effect is the reset, not a derived value.
  useEffect(() => { setInvestorOverlay(false); setLandingBg(null) }, [pathname])

  // Get colors for current page, default to landing
  const baseColors = pageColors[pathname as keyof typeof pageColors] || pageColors['/']
  const colors = investorOverlay
    ? { ...baseColors, accent: COLORS.neutral8, logoColor: COLORS.neutral8 }
    : baseColors

  // Nav links rest color: white on dark page backgrounds, dark on light ones
  const navTextColor = (() => {
    const hex = colors.background.replace('#', '')
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return lum > 0.6 ? COLORS.neutral9 : COLORS.neutral1
  })()

  // Scroll to top on route change
  useEffect(() => {
    // Always reset via window.scrollTo as a baseline (covers Lenis-stopped states)
    window.scrollTo(0, 0)
    // Also reset via Lenis if active
    if (lenis) {
      lenis.scrollTo(0, { immediate: true })
    }
    // Clear any page-level CSS scroll snap from html
    document.documentElement.style.scrollSnapType = ''
  }, [pathname, lenis])

  // Update document meta on route change (refs cached once)
  const metaRefs = useRef({
    desc: document.querySelector('meta[name="description"]'),
    author: document.querySelector('meta[name="author"]'),
    ogType: document.querySelector('meta[property="og:type"]'),
    ogTitle: document.querySelector('meta[property="og:title"]'),
    ogDesc: document.querySelector('meta[property="og:description"]'),
    ogUrl: document.querySelector('meta[property="og:url"]'),
    ogImage: document.querySelector('meta[property="og:image"]'),
    ogImageAlt: document.querySelector('meta[property="og:image:alt"]'),
    robots: document.querySelector('meta[name="robots"]'),
    twTitle: document.querySelector('meta[name="twitter:title"]'),
    twDesc: document.querySelector('meta[name="twitter:description"]'),
    twImage: document.querySelector('meta[name="twitter:image"]'),
    canonical: document.querySelector('link[rel="canonical"]'),
    hreflang: document.querySelector('link[rel="alternate"][hreflang]'),
  })
  useEffect(() => {
    // The hosting layer 301s directory routes to a trailing slash (/platform →
    // /platform/), so normalize before any lookup — otherwise every slashed
    // path misses ROUTE_META and clobbers the prerendered head with home meta.
    const path = pathname.replace(/\/+$/, '') || '/'
    // Blog posts and archived press releases own their own head (set in the
    // route via applyBlogMeta / applyPressMeta) — don't clobber them with the
    // home-meta fallback. The index routes (/blog, /press) are known static
    // routes and handled normally.
    if (path.startsWith('/blog/') || path.startsWith('/press/')) return
    const meta = routeMeta[path] || routeMeta['/']!
    // On error pages the matched pathname has no real meta (404 falls back to
    // home), so use the title the error page reported instead of clobbering it.
    const title = errorPage.active ? (errorPage.title ?? 'Error — Ent') : meta.title
    // /press is the evergreen "latest release" landing — canonicalize it to the
    // latest release's permanent permalink so that stable URL is the indexed one.
    const url = path === '/press' ? pressIndexCanonicalUrl() : canonicalUrl(path)
    const r = metaRefs.current
    document.title = title
    r.desc?.setAttribute('content', meta.description)
    r.ogTitle?.setAttribute('content', title)
    r.ogDesc?.setAttribute('content', meta.description)
    r.ogUrl?.setAttribute('content', url)
    r.twTitle?.setAttribute('content', title)
    r.twDesc?.setAttribute('content', meta.description)
    r.canonical?.setAttribute('href', url)
    r.hreflang?.setAttribute('href', url)
    // Reset the article-only overrides a blog post may have left behind so a
    // static page never inherits a post's author / og:type / cover image on SPA nav.
    r.author?.setAttribute('content', SITE.name)
    r.ogType?.setAttribute('content', 'website')
    // Unlisted routes (direct-link-only, absent from the sitemap) stay noindex
    // after hydration too — a crawler that executes JS must see the same
    // directive the prerendered HTML carried. Every other route resets to the
    // shell default so an unlisted page never leaves noindex behind on SPA nav.
    r.robots?.setAttribute(
      'content',
      isUnlistedRoute(path) ? 'noindex, nofollow' : 'index, follow',
    )
    // Per-route OG image when set, else the default.
    const ogImage = meta.image ?? SITE.ogImage
    r.ogImage?.setAttribute('content', ogImage)
    r.ogImageAlt?.setAttribute('content', title)
    r.twImage?.setAttribute('content', ogImage)
  }, [pathname, errorPage.active, errorPage.title])

  return (
    <QueryProvider>
      <ThemeProvider>
        <ConsentProvider>
        <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', overflowX: 'clip', position: 'relative' }}>
          <FontLoader />

          {/* Animated background */}
          <motion.div
            animate={{ backgroundColor: (isLanding && landingBg) ? landingBg : colors.background }}
            transition={{ duration: (isLanding && landingBg) ? 0 : 1, ease: EASE_OUT }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
            }}
          />

          {/* Progressive blur at the top edge — above content (z10), below navbar (z100) */}
          <ProgressiveBlurTop height={180} zIndex={50} />

          {/* Persistent header / top navigation */}
          <header
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: 88,
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              padding: '0 clamp(24px, 3.5vw, 48px)',
              zIndex: 100,
            }}
          >
            {/* Wordmark — left column */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1, ease: EASE_OUT }}
              style={{ justifySelf: 'start' }}
            >
              <AnimatePresence>
                {!isContactSubmitted && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Link
                      to="/"
                      aria-label="Go to home page"
                      style={{ ...resetButton, display: 'inline-flex', alignItems: 'center' }}
                    >
                      {/* Rune icon — full lockup on every page; on the homepage it
                          pushes in once scrolled past the hero. */}
                      <motion.div
                        aria-hidden
                        initial={false}
                        animate={{
                          width: showRune ? 30 : 0,
                          opacity: showRune ? 1 : 0,
                          marginRight: showRune ? 10 : 0,
                        }}
                        transition={{ duration: 0.5, ease: EASE_OUT }}
                        style={{ height: '30px', overflow: 'hidden', flex: '0 0 auto' }}
                      >
                        <motion.div
                          animate={{ backgroundColor: colors.logoColor }}
                          transition={{ duration: 1, ease: EASE_OUT }}
                          style={{
                            width: '30px',
                            height: '30px',
                            WebkitMaskImage: `url(${import.meta.env.BASE_URL}images/rune-mark.svg)`,
                            maskImage: `url(${import.meta.env.BASE_URL}images/rune-mark.svg)`,
                            WebkitMaskSize: 'contain',
                            maskSize: 'contain',
                            WebkitMaskRepeat: 'no-repeat',
                            maskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            maskPosition: 'center',
                          }}
                        />
                      </motion.div>
                      {/* Wordmark */}
                      <motion.div
                        animate={{ backgroundColor: colors.logoColor }}
                        transition={{ duration: 1, ease: EASE_OUT }}
                        style={{
                          width: '74px',
                          height: '30px',
                          flex: '0 0 auto',
                          WebkitMaskImage: `url(${import.meta.env.BASE_URL}images/ent_wordmark.svg)`,
                          maskImage: `url(${import.meta.env.BASE_URL}images/ent_wordmark.svg)`,
                          WebkitMaskSize: 'contain',
                          maskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'left center',
                          maskPosition: 'left center',
                        }}
                      />
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Center nav links — middle column (page-centered), desktop only */}
            {!isMobile && (
              <motion.nav
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 1, ease: EASE_OUT }}
                style={{
                  gridColumn: 2,
                  justifySelf: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '40px',
                }}
              >
                {([
                  { label: 'Platform', to: '/platform/' },
                  {
                    label: 'Resources',
                    options: [
                      { label: 'Blog', to: '/blog/' },
                      { label: 'News', to: '/news/' },
                      { label: 'Press Release', to: '/press/' },
                    ],
                  },
                  { label: 'Company', to: '/company/' },
                ] as Array<{
                  label: string
                  caret?: boolean
                  to?: string
                  options?: { label: string; to: string }[]
                }>).map(({ label, caret, to, options }) =>
                  options ? (
                    <NavDropdown key={label} label={label} accentColor={colors.accent} restColor={navTextColor} options={options} />
                  ) : (
                    <NavLink key={label} label={label} accentColor={colors.accent} restColor={navTextColor} caret={caret} to={to} />
                  ),
                )}
              </motion.nav>
            )}

            {/* Right cluster — demo CTA (desktop) / hamburger (mobile) */}
            <div style={{ gridColumn: 3, justifySelf: 'end', display: 'flex', alignItems: 'center', gap: '16px', zIndex: 101 }}>
              {!isMobile && (
                <AnimatePresence>
                  {!isContactSubmitted && (
                    <motion.div
                      initial={{ opacity: 0, y: -30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.2, delay: 1, ease: EASE_OUT }}
                    >
                      <CtaButton
                        label="Schedule a Demo"
                        variant="solid"
                        to="/contact/"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {/* Mobile-only demo link, sat to the left of the hamburger.
                  Hidden while the menu is open (the overlay lists it there). */}
              {isMobile && !mobileMenuOpen && !isContactSubmitted && (
                <CtaButton
                  label="Schedule a Demo"
                  variant="solid"
                  to="/contact/"
                  fontSize="var(--text-xs)"
                  height={44}
                />
              )}

              {isMobile && (
                <button
                  onClick={() => (mobileMenuOpen ? closeMobileMenu() : setMobileMenuOpen(true))}
                  aria-label="Toggle menu"
                  style={{
                    ...resetButton,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '44px',
                    height: '44px',
                  }}
                >
                  <motion.span
                    animate={{ rotate: mobileMenuOpen ? 45 : 0, y: mobileMenuOpen ? 8 : 0, backgroundColor: colors.accent }}
                    transition={{ duration: 0.3, ease: EASE_OUT }}
                    style={{ display: 'block', width: '22px', height: '2px', borderRadius: '1px' }}
                  />
                  <motion.span
                    animate={{ opacity: mobileMenuOpen ? 0 : 1, backgroundColor: colors.accent }}
                    transition={{ duration: 0.2, ease: EASE_OUT }}
                    style={{ display: 'block', width: '22px', height: '2px', borderRadius: '1px', marginTop: '6px' }}
                  />
                  <motion.span
                    animate={{ rotate: mobileMenuOpen ? -45 : 0, y: mobileMenuOpen ? -8 : 0, backgroundColor: colors.accent }}
                    transition={{ duration: 0.3, ease: EASE_OUT }}
                    style={{ display: 'block', width: '22px', height: '2px', borderRadius: '1px', marginTop: '6px' }}
                  />
                </button>
              )}
            </div>
          </header>

          {/* Fixed bottom-center slot for the about page's advisor-carousel nav.
              The persistent footer chrome (©/Legal/social/wordmark) was replaced
              by the global <SiteFooter/> rendered in-flow at the end of <main>. */}
          <div
            id="footer-center-slot"
            style={{
              position: 'fixed',
              bottom: 30,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              zIndex: 100,
            }}
          />

          {/* Mobile menu overlay */}
          <AnimatePresence>
            {mobileMenuOpen && isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: EASE_OUT }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: `rgba(${NEUTRAL8_RGB}, 0.8)`,
                  backdropFilter: 'blur(15px)',
                  WebkitBackdropFilter: 'blur(15px)',
                  zIndex: 99,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0',
                }}
              >
                {/* Nav links */}
                {([
                  { label: 'Platform', to: '/platform/' },
                  {
                    label: 'Resources',
                    options: [
                      { label: 'Blog', to: '/blog/' },
                      { label: 'News', to: '/news/' },
                      { label: 'Press Release', to: '/press/' },
                    ],
                  },
                  { label: 'Company', to: '/company/' },
                ] as Array<{
                  label: string
                  to?: string
                  options?: { label: string; to: string }[]
                }>).map(({ label, to, options }, i) => {
                  const itemStyle: React.CSSProperties = {
                    ...resetButton,
                    textDecoration: 'none',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 400,
                    fontFamily: BODY_FONT,
                    color: COLORS.green1,
                    lineHeight: '34px',
                    padding: '24px 0',
                  }
                  const transition = { duration: 0.3, delay: (i + 1) * 0.05, ease: EASE_OUT }

                  if (options) {
                    return (
                      <motion.div
                        key={label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={transition}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                      >
                        <button
                          onClick={() => setResourcesOpen(prev => !prev)}
                          aria-expanded={resourcesOpen}
                          style={{
                            ...itemStyle,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          {label}
                          <motion.span
                            animate={{ rotate: resourcesOpen ? 180 : 0 }}
                            transition={{ duration: 0.3, ease: EASE_OUT }}
                            style={{ display: 'inline-flex', fontSize: 'var(--text-base)' }}
                          >
                            ▾
                          </motion.span>
                        </button>
                        <AnimatePresence initial={false}>
                          {resourcesOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: EASE_OUT }}
                              style={{
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              {options.map(opt => (
                                <MotionLink
                                  key={opt.label}
                                  to={opt.to}
                                  onClick={closeMobileMenu}
                                  style={{
                                    ...resetButton,
                                    textDecoration: 'none',
                                    fontSize: 'var(--text-2xl)',
                                    fontWeight: 400,
                                    fontFamily: BODY_FONT,
                                    color: COLORS.green1,
                                    opacity: 0.8,
                                    lineHeight: '28px',
                                    padding: '12px 0',
                                  }}
                                >
                                  {opt.label}
                                </MotionLink>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )
                  }

                  return (
                    <MotionLink
                      key={label}
                      to={to}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={transition}
                      onClick={closeMobileMenu}
                      style={itemStyle}
                    >
                      {label}
                    </MotionLink>
                  )
                })}

                {/* Schedule a Demo — the header's solid pill button, not a
                    plain text link like the nav items above. */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3, delay: 0.2, ease: EASE_OUT }}
                  style={{ padding: '24px 0' }}
                  onClick={closeMobileMenu}
                >
                  <CtaButton label="Schedule a Demo" variant="solid" to="/contact/" />
                </motion.div>

                {/* Social links — pinned near the bottom of the overlay
                    (absolute) so they stay anchored while the nav stays centred. */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.2, ease: EASE_OUT }}
                  style={{
                    position: 'absolute',
                    bottom: 'calc(48px + env(safe-area-inset-bottom))',
                    left: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '24px',
                  }}
                >
                  <a
                    href="https://x.com/Ent_Security"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 400,
                      fontFamily: BODY_FONT,
                      color: COLORS.green1,
                      textDecoration: 'none',
                    }}
                  >
                    X
                  </a>
                  <a
                    href="https://www.linkedin.com/company/ent-security"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 400,
                      fontFamily: BODY_FONT,
                      color: COLORS.green1,
                      textDecoration: 'none',
                    }}
                  >
                    LinkedIn
                  </a>

                  {/* Copyright — grouped at the bottom of the social links */}
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.25, ease: EASE_OUT }}
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 400,
                      fontFamily: BODY_FONT,
                      color: COLORS.green1,
                    }}
                  >
                    © {new Date().getFullYear()}
                  </motion.span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Page content. Always in-flow (relative content, visible overflow) so
              every route's content contributes height — the in-flow <SiteFooter/>
              sits below it and the page scrolls. There is deliberately no
              per-route allow-list here: any absolute/clipped variant only ever
              breaks scroll and drops the footer onto the content. */}
          <main style={{
            position: 'relative',
            zIndex: 10,
            overflow: 'visible',
            boxSizing: 'border-box',
            minHeight: 'calc(100 * var(--vh))'
          }}>
            <motion.div
              key={pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, ease: EASE_OUT }}
              style={{
                position: 'relative',
                width: '100%'
              }}
            >
              <Outlet />
            </motion.div>

            {/* Global footer — in-flow at the bottom of every page (hidden on error pages) */}
            {!isErrorPage && <SiteFooter />}
          </main>

          {/* Cookie consent banner + preferences modal (client-only) */}
          <CookieConsent />

          {/* HubSpot analytics — loads only after marketing consent */}
          <HubSpotTracking />
        </div>
        </ConsentProvider>
      </ThemeProvider>
    </QueryProvider>
  )
}
